// Content update functionality for preview popup
import { escapeHtml, detectMeta, encodeFilePath } from '../../../../modules/files/file-list.utils.js';
import { setPreviewState, getPreviewState } from './state.js';
import { resetPinButton, setupAutoPinHandlers, cleanupAutoPinHandlers } from './pin.js';

async function loadPdfWithProgress(previewUrl, contentDiv, state, setPinned) {
	// Use setPinned from parameter or from state (for document switching)
	const currentState = getPreviewState();
	const setPinnedFn = setPinned || state.setPinnedCallback || currentState.setPinnedCallback;
	
	// Show spinner immediately
	const loadingDiv = contentDiv.find('.preview-popup__loading');
	loadingDiv.css('display', 'flex').show();
	
	// Create progress bar container and add it to loading div (under spinner)
	const progressContainer = $(`
		<div class="preview-popup__progress">
			<div class="preview-popup__progress-bar">
				<div class="preview-popup__progress-fill"></div>
			</div>
			<div class="preview-popup__progress-text">0%</div>
		</div>
	`);
	loadingDiv.append(progressContainer);
	
	try {
		const response = await fetch(previewUrl);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		
		const contentLength = response.headers.get('content-length');
		const total = contentLength ? parseInt(contentLength, 10) : 0;
		
		if (!response.body) {
			throw new Error('ReadableStream not supported');
		}
		
		const reader = response.body.getReader();
		const chunks = [];
		let receivedLength = 0;
		
		while (true) {
			const { done, value } = await reader.read();
			
			if (done) break;
			
			chunks.push(value);
			receivedLength += value.length;
			
			// Update progress
			if (total > 0) {
				const percent = Math.round((receivedLength / total) * 100);
				const progressFill = progressContainer.find('.preview-popup__progress-fill');
				const progressText = progressContainer.find('.preview-popup__progress-text');
				progressFill.css('width', percent + '%');
				progressText.text(percent + '%');
			} else {
				// If content-length is unknown, show indeterminate progress
				const progressText = progressContainer.find('.preview-popup__progress-text');
				progressText.text(Math.round(receivedLength / 1000) + ' KB');
			}
		}
		
		// Combine chunks into single Uint8Array
		const allChunks = new Uint8Array(receivedLength);
		let position = 0;
		for (const chunk of chunks) {
			allChunks.set(chunk, position);
			position += chunk.length;
		}
		
		// Create blob and object URL
		const blob = new Blob([allChunks], { type: 'application/pdf' });
		const blobUrl = URL.createObjectURL(blob);
		
		// Remove progress bar
		progressContainer.remove();
		
		// Create iframe with blob URL
		// Add tabindex="-1" so iframe can receive focus programmatically
		const iframe = $(`
			<iframe src="${escapeHtml(blobUrl)}" frameborder="0" class="preview-popup__iframe" loading="lazy" tabindex="-1"></iframe>
		`);
		
		// Create transparent overlay to intercept clicks for PDF pinning
		const clickOverlay = $(`
			<div class="preview-popup__pdf-click-overlay"></div>
		`);
		
		// Clean up old iframe and handlers if exists
		const oldIframe = contentDiv.find('.preview-popup__iframe');
		if (oldIframe.length > 0) {
			// Remove all event handlers before removing element
			oldIframe.off();
		}
		contentDiv.find('.preview-popup__iframe, .preview-popup__pdf-click-overlay').remove();
		contentDiv.prepend(iframe);
		contentDiv.append(clickOverlay);
		
		// Setup wheel event handlers for PDF - these must be set up ALWAYS, not just when setPinned exists
		
		// Handle wheel events on contentDiv with capture phase
		// This catches wheel events BEFORE overlay can block them
		contentDiv.off('wheel.preview-pdf-pin').on('wheel.preview-pdf-pin', function(e) {
			const target = e.target;
			
			// If wheel happens anywhere in contentDiv (including over overlay),
			// pin the preview and immediately disable overlay so wheel can reach PDF
			// Always get setPinned from state (it's updated when switching documents)
				const state = getPreviewState();
			const currentSetPinnedFn = state.setPinnedCallback || setPinnedFn;
			if (currentSetPinnedFn && !state.previewPopupPinned) {
				currentSetPinnedFn(true);
					// Immediately disable overlay so wheel events can reach PDF
					clickOverlay.css('pointer-events', 'none');
			}
			
			// Prevent wheel events from propagating to main window
			// This allows PDF to scroll without scrolling the main page
			e.stopPropagation();
			// Don't prevent default - let PDF handle scrolling internally
		});
		
		// Also prevent wheel events on iframe itself
		iframe.off('wheel.preview-pdf-scroll').on('wheel.preview-pdf-scroll', function(e) {
			e.stopPropagation();
		});
		
		// Prevent wheel events on overlay as well
		clickOverlay.off('wheel.preview-pdf-scroll').on('wheel.preview-pdf-scroll', function(e) {
			e.stopPropagation();
		});
		
		// Setup auto-pin handlers for PDF iframe after it loads
		// Always use setPinnedCallback from state to ensure it's current
			const currentState = getPreviewState();
		const currentSetPinnedFn = currentState.setPinnedCallback || setPinnedFn;
		if (currentSetPinnedFn) {
			if (currentState.previewPopupElement && currentState.previewPopupElement.length) {
				// Setup handlers when iframe loads
				iframe.off('load.preview-pdf-pin').on('load.preview-pdf-pin', function() {
					setupAutoPinHandlers(iframe, currentState.previewPopupElement, currentSetPinnedFn);
				});
				
				// Click overlay to intercept clicks and pin PDF
				// Only pin on real user clicks, not programmatic clicks
				clickOverlay.off('click.preview-pdf-pin mousedown.preview-pdf-pin').on('click.preview-pdf-pin mousedown.preview-pdf-pin', function(e) {
					// Ignore programmatic clicks (isTrusted: false)
					if (!e.isTrusted) return;
					
					const state = getPreviewState();
					const currentSetPinnedFn2 = state.setPinnedCallback || currentSetPinnedFn;
					if (!state.previewPopupPinned && currentSetPinnedFn2) {
						currentSetPinnedFn2(true);
						// After pinning, make overlay non-interactive so clicks pass through to PDF
						clickOverlay.css('pointer-events', 'none');
					}
				});
				
				// Also add click handler directly on iframe element for PDF (cross-origin fallback)
				// Only pin on real user clicks, not programmatic clicks
				iframe.off('click.preview-pdf-pin-element mousedown.preview-pdf-pin-element').on('click.preview-pdf-pin-element mousedown.preview-pdf-pin-element', function(e) {
					// Ignore programmatic clicks (isTrusted: false)
					if (!e.isTrusted) return;
					
					const state = getPreviewState();
					const currentSetPinnedFn2 = state.setPinnedCallback || currentSetPinnedFn;
					if (!state.previewPopupPinned && currentSetPinnedFn2) {
						currentSetPinnedFn2(true);
						clickOverlay.css('pointer-events', 'none');
					}
				});
				
				// Add click handler on content container for PDF
				// Only pin on real user clicks, not programmatic clicks
				contentDiv.off('click.preview-pdf-pin mousedown.preview-pdf-pin').on('click.preview-pdf-pin mousedown.preview-pdf-pin', function(e) {
					// Ignore programmatic clicks (isTrusted: false)
					if (!e.isTrusted) return;
					
					// Only pin if clicking on iframe or empty space
					if ($(e.target).hasClass('preview-popup__iframe') || e.target === this || $(e.target).closest('.preview-popup__iframe').length > 0) {
						const state = getPreviewState();
						const currentSetPinnedFn2 = state.setPinnedCallback || currentSetPinnedFn;
						if (!state.previewPopupPinned && currentSetPinnedFn2) {
							currentSetPinnedFn2(true);
							clickOverlay.css('pointer-events', 'none');
						}
					}
				});
			}
		}
		
		// Hide loading spinner when iframe loads
		// Clean up any existing handlers first to avoid conflicts when switching PDFs
		iframe.off('load.preview-pdf load.preview-pdf-pin');
		
		// Function to activate iframe for scrolling
		const activateIframeForScroll = () => {
			try {
				const iframeEl = iframe[0];
				if (!iframeEl || !document.body.contains(iframeEl)) {
					return false; // Iframe was removed
				}
				
				// Focus iframe first
				iframeEl.focus();
				
				// Get iframe center coordinates
				const rect = iframeEl.getBoundingClientRect();
				const centerX = rect.left + rect.width / 2;
				const centerY = rect.top + rect.height / 2;
				
				// Create and dispatch click event at center of iframe
				const clickEvent = new MouseEvent('click', {
					bubbles: true,
					cancelable: true,
					view: window,
					clientX: centerX,
					clientY: centerY,
					button: 0
				});
				
				iframeEl.dispatchEvent(clickEvent);
				
				// Also try to focus content window
				if (iframeEl.contentWindow) {
					iframeEl.contentWindow.focus();
				}
				
				// Disable overlay immediately after activation so wheel events can reach iframe
				// This is critical for scroll to work when switching between PDFs
				clickOverlay.css('pointer-events', 'none');
				
				return true;
			} catch (e) {
				// Cross-origin restriction or other error - that's ok
				console.debug('[Preview] Could not auto-click PDF iframe:', e);
				return false;
			}
		};
		
		iframe.on('load.preview-pdf', function() {
			loadingDiv.fadeOut(200);
			
			// Disable overlay immediately when iframe loads so wheel events can reach iframe
			// This is critical for scroll to work when switching between PDFs
			clickOverlay.css('pointer-events', 'none');
			
			// Simulate click on PDF iframe to activate it so scroll works immediately
			// Use multiple attempts with increasing delays to ensure activation
			setTimeout(() => {
				activateIframeForScroll();
			}, 100);
			
			setTimeout(() => {
				activateIframeForScroll();
			}, 300);
			
			setTimeout(() => {
				activateIframeForScroll();
			}, 500);
			
			// Clean up blob URL after a delay to allow iframe to load
			setTimeout(() => {
				// Keep blob URL alive while iframe is visible
				// It will be cleaned up when popup closes
			}, 1000);
		});
		
		// Also activate iframe on mouseenter to ensure scroll works when user hovers
		// This is especially important when switching between PDFs
		contentDiv.off('mouseenter.preview-pdf-activate').on('mouseenter.preview-pdf-activate', function() {
			const currentIframe = contentDiv.find('.preview-popup__iframe');
			if (currentIframe.length > 0) {
				// Try to activate iframe when mouse enters preview area
				setTimeout(() => {
					try {
						const iframeEl = currentIframe[0];
						if (iframeEl && document.body.contains(iframeEl)) {
							iframeEl.focus();
							if (iframeEl.contentWindow) {
								iframeEl.contentWindow.focus();
							}
							
							// Get iframe center coordinates
							const rect = iframeEl.getBoundingClientRect();
							const centerX = rect.left + rect.width / 2;
							const centerY = rect.top + rect.height / 2;
							
							// Create and dispatch click event at center of iframe
							const clickEvent = new MouseEvent('click', {
								bubbles: true,
								cancelable: true,
								view: window,
								clientX: centerX,
								clientY: centerY,
								button: 0
							});
							
							iframeEl.dispatchEvent(clickEvent);
						}
					} catch (e) {
						// Cross-origin restriction or other error - that's ok
						console.debug('[Preview] Could not auto-click PDF iframe on mouseenter:', e);
					}
				}, 50);
			}
		});
		
		// Store blob URL for cleanup
		state.pdfBlobUrl = blobUrl;
		setPreviewState(state);
		
	} catch (error) {
		console.error('[Preview] PDF load error:', error);
		progressContainer.remove();
		loadingDiv.fadeOut(200);
		contentDiv.html(`
			<div class="preview-popup__error">
				<p>Failed to load PDF</p>
				<p style="font-size: 0.875rem; color: var(--color-text-muted);">${escapeHtml(error.message)}</p>
			</div>
		`);
	}
}

export function updatePreviewContent(file, fileKey, setPinned) {
	const state = getPreviewState();
	
	if (!state.previewPopupElement || !state.previewPopupElement.length) {
		console.warn('[Preview Content] updatePreviewContent aborted - no popup element');
		return;
	}
	
	// Use setPinned from parameter or from state (for document switching)
	const setPinnedFn = setPinned || state.setPinnedCallback;
	
	// Ensure setPinnedCallback is in state (it should be set when popup is created, but double-check)
	if (!state.setPinnedCallback && setPinnedFn) {
		state.setPinnedCallback = setPinnedFn;
		setPreviewState(state);
	}
	
	const meta = detectMeta(file.p || file.path || '', file.n || file.name || '');
	const extLower = (meta.ext || '').toLowerCase();
	const isMP3 = extLower === 'mp3';
	if (isMP3) {
		return; // MP3 files are handled by audio-player.js
	}
	
	const isPDF = extLower === 'pdf';
	const isDoc = extLower === 'doc' || extLower === 'docx';
	const isOT = !!(file.ot || file.ot_level || (typeof file.category === 'string' && file.category.toUpperCase() === 'OT'));
	const hasOnline = !!(file.o === 1 || file.online === 1);
	const fileName = file.n || file.name || 'Unknown';
	const baseUrl = window.location.origin;
	
	let previewUrl = '';
	const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
	const n = (file.n || file.name || '');
	const rel = `${p}${p ? '/' : ''}${n}`;
	
	// For files with hasOnline OR doc/docx files, ALWAYS use /online/ URL, never /library/ (which downloads)
	if (hasOnline || isDoc) {
		// Always use /online/ for preview, even if file._url points to /library/
		previewUrl = `${baseUrl}/online/${encodeFilePath(rel)}`;
		// Preserve version parameter from file._url if available
		if (file._url && file._url.includes('?v=')) {
			const versionParam = file._url.split('?v=')[1].split('&')[0]; // Get version, ignore other params
			previewUrl += `?v=${versionParam}`;
		}
	} else if (file._url && !file._url.includes('/library/')) {
		// Use server-provided _url only if it's not a /library/ URL (for non-online files)
		previewUrl = `${baseUrl}${file._url}`;
	} else {
		// Fallback to old logic if _url not provided or is /library/ URL
		if (isOT) {
			previewUrl = `${baseUrl}/library/${encodeFilePath(rel)}?ot=1`;
		} else {
			previewUrl = `${baseUrl}/library/${encodeFilePath(rel)}`;
		}
	}
	
	state.previewPopupElement.find('.preview-popup__title').text(fileName);
	// Notify header actions + nav counter that the previewed document changed
	// (single switch point for hover-switch AND keyboard nav).
	try { document.dispatchEvent(new CustomEvent('preview:switched')); } catch (e) {}

	// Reset pinned state when switching documents
	state.previewPopupPinned = false;
	setPreviewState(state);
	
	// Clear any pending timeout when switching documents
	if (state.previewPopupTimeout) {
		clearTimeout(state.previewPopupTimeout);
		state.previewPopupTimeout = null;
		setPreviewState(state);
	}
	
	// Reset hover states to ensure proper auto-close behavior
	state.previewPopupHovered = false;
	state.previewIconHovered = false;
	setPreviewState(state);
	
	// Update pin button state to reflect reset
	if (state.previewPopupElement && state.previewPopupElement.length) {
		const pinButton = state.previewPopupElement.find('.preview-popup__pin');
		resetPinButton(pinButton);
	}
	
	const contentDiv = state.previewPopupElement.find('.preview-popup__content');
	
	// Clean up old PDF blob URL if exists
	if (state.pdfBlobUrl) {
		URL.revokeObjectURL(state.pdfBlobUrl);
		state.pdfBlobUrl = null;
		setPreviewState(state);
	}
	
	// Clean up all event handlers on contentDiv before switching documents
	// This is critical to prevent conflicts when switching between PDFs
	
	// Remove native event listeners if they exist
	const wheelHandler = contentDiv.data('wheel-handler');
	if (wheelHandler && contentDiv[0]) {
		contentDiv[0].removeEventListener('wheel', wheelHandler, { capture: true });
		contentDiv.removeData('wheel-handler');
	}
	
	contentDiv.off('wheel.preview-pdf-pin wheel.preview-pdf-scroll wheel.preview-scroll click.preview-pdf-pin mousedown.preview-pdf-pin click.preview-pin mouseenter.preview-pdf-activate');
	
	// Clean up old iframe handlers if exists
	const oldIframe = contentDiv.find('.preview-popup__iframe');
	if (oldIframe.length > 0) {
		oldIframe.off();
		cleanupAutoPinHandlers(oldIframe, state.previewPopupElement);
	}
	
	if (isPDF) {
		// For PDF files, use fetch with progress tracking
		contentDiv.html(`
			<div class="preview-popup__loading">
				<div class="spinner-ultra" aria-label="Loading" title="Loading"></div>
			</div>
		`);
		loadPdfWithProgress(previewUrl, contentDiv, state, setPinnedFn);
		} else {
			// For other files, use regular iframe loading
			const iframe = contentDiv.find('.preview-popup__iframe');
			if (iframe.length === 0) {
				contentDiv.html(`
					<iframe src="${escapeHtml(previewUrl)}" frameborder="0" class="preview-popup__iframe" loading="lazy" tabindex="-1" style="overscroll-behavior: contain;"></iframe>
					<div class="preview-popup__loading">
						<div class="spinner-ultra" aria-label="Loading" title="Loading"></div>
					</div>
				`);
				const newIframe = contentDiv.find('.preview-popup__iframe');
				newIframe.off('load.preview-switch').on('load.preview-switch', function() {
					contentDiv.find('.preview-popup__loading').fadeOut(200);
					
					// Try to apply overscroll-behavior to iframe content (if same-origin)
					try {
						const iframeEl = newIframe[0];
						if (iframeEl && iframeEl.contentWindow && iframeEl.contentWindow.document) {
							const iframeDoc = iframeEl.contentWindow.document;
							const iframeBody = iframeDoc.body;
							const iframeHtml = iframeDoc.documentElement;
							
							// Apply overscroll-behavior to prevent scroll chaining
							if (iframeBody) {
								iframeBody.style.overscrollBehavior = 'contain';
							}
							if (iframeHtml) {
								iframeHtml.style.overscrollBehavior = 'contain';
							}
						}
					} catch (e) {
						// Cross-origin restriction - can't access iframe content
						console.debug('[Preview] Cannot apply overscroll-behavior to iframe content (cross-origin):', e);
					}
					
					// Setup auto-pin handlers AFTER iframe loads
					// Always use setPinnedCallback from state to ensure it's current
					const currentState = getPreviewState();
					const currentSetPinnedFn = currentState.setPinnedCallback || setPinnedFn;
					if (currentSetPinnedFn) {
						if (currentState.previewPopupElement && currentState.previewPopupElement.length) {
							setupAutoPinHandlers(newIframe, currentState.previewPopupElement, currentSetPinnedFn);
						}
					}
				});
				
				// Use same scroll logic as PDF: only stopPropagation, no preventDefault
				// This allows iframe to scroll internally while preventing scroll chaining to main window
				newIframe.off('wheel.preview-scroll').on('wheel.preview-scroll', function(e) {
					// Stop propagation to prevent scroll chaining to main window
					e.stopPropagation();
				});
				
				// Handle wheel events on contentDiv to prevent scroll chaining
				// Use capture phase to catch events, but only stop propagation, don't prevent default
				// This allows iframe to handle scrolling while preventing scroll chaining
				contentDiv.off('wheel.preview-scroll');
				const wheelHandler = function(e) {
					// Auto-pin on scroll (user action) - always get setPinned from state (it's updated when switching documents)
					const state = getPreviewState();
					const currentSetPinnedFn = state.setPinnedCallback || setPinnedFn;
					if (!state.previewPopupPinned && currentSetPinnedFn) {
						currentSetPinnedFn(true);
					}
					
					// Only stop propagation to prevent scroll chaining to main window
					// Don't prevent default - let iframe handle scrolling internally
					// CSS overscroll-behavior: contain on iframe will prevent scroll chaining
					e.stopPropagation();
				};
				contentDiv[0].addEventListener('wheel', wheelHandler, { capture: true, passive: true });
				
				// Store handler reference for cleanup
				if (!contentDiv.data('wheel-handler')) {
					contentDiv.data('wheel-handler', wheelHandler);
				}
			} else {
				contentDiv.find('.preview-popup__loading').css('display', 'flex').show();
				// Set overscroll-behavior on iframe to prevent scroll chaining
				iframe.css('overscroll-behavior', 'contain');
				iframe.attr('src', previewUrl);
				iframe.off('load.preview-switch').on('load.preview-switch', function() {
					contentDiv.find('.preview-popup__loading').fadeOut(200);
					
					// Try to apply overscroll-behavior to iframe content (if same-origin)
					try {
						const iframeEl = iframe[0];
						if (iframeEl && iframeEl.contentWindow && iframeEl.contentWindow.document) {
							const iframeDoc = iframeEl.contentWindow.document;
							const iframeBody = iframeDoc.body;
							const iframeHtml = iframeDoc.documentElement;
							
							// Apply overscroll-behavior to prevent scroll chaining
							if (iframeBody) {
								iframeBody.style.overscrollBehavior = 'contain';
							}
							if (iframeHtml) {
								iframeHtml.style.overscrollBehavior = 'contain';
							}
						}
					} catch (e) {
						// Cross-origin restriction - can't access iframe content
						console.debug('[Preview] Cannot apply overscroll-behavior to iframe content (cross-origin):', e);
					}
					
					// Setup auto-pin handlers AFTER iframe loads
					// Always use setPinnedCallback from state to ensure it's current
					const currentState = getPreviewState();
					const currentSetPinnedFn = currentState.setPinnedCallback || setPinnedFn;
					if (currentSetPinnedFn) {
						if (currentState.previewPopupElement && currentState.previewPopupElement.length) {
							setupAutoPinHandlers(iframe, currentState.previewPopupElement, currentSetPinnedFn);
						}
					}
				});
				
				// Use same scroll logic as PDF: only stopPropagation, no preventDefault
				// This allows iframe to scroll internally while preventing scroll chaining to main window
				iframe.off('wheel.preview-scroll').on('wheel.preview-scroll', function(e) {
					// Stop propagation to prevent scroll chaining to main window
					e.stopPropagation();
				});
				
				// Handle wheel events on contentDiv to prevent scroll chaining
				// Use capture phase to catch events, but only stop propagation, don't prevent default
				// This allows iframe to handle scrolling while preventing scroll chaining
				contentDiv.off('wheel.preview-scroll');
				const wheelHandler = function(e) {
					// Auto-pin on scroll (user action) - always get setPinned from state (it's updated when switching documents)
					const state = getPreviewState();
					const currentSetPinnedFn = state.setPinnedCallback || setPinnedFn;
					if (!state.previewPopupPinned && currentSetPinnedFn) {
						currentSetPinnedFn(true);
					}
					
					// Only stop propagation to prevent scroll chaining to main window
					// Don't prevent default - let iframe handle scrolling internally
					// CSS overscroll-behavior: contain on iframe will prevent scroll chaining
					e.stopPropagation();
				};
				contentDiv[0].addEventListener('wheel', wheelHandler, { capture: true, passive: true });
				
				// Store handler reference for cleanup
				if (!contentDiv.data('wheel-handler')) {
					contentDiv.data('wheel-handler', wheelHandler);
				}
			}
		}
}

export function buildPreviewUrl(file) {
	// Check if it's an MP3 file - MP3 files should not have preview URLs
	const filePath = file.p || file.path || '';
	const fileName = file.n || file.name || '';
	const meta = detectMeta(filePath, fileName);
	const extLower = (meta.ext || '').toLowerCase();
	const isMP3 = extLower === 'mp3';
	
	if (isMP3) {
		// MP3 files are handled by audio-player.js, not preview popup
		// Return empty string to prevent preview from opening
		if (window.debugMode === '1') {
			console.warn('[Preview] buildPreviewUrl called for MP3 file, returning empty URL:', {
				filePath,
				fileName,
				fileKey: (filePath || '') + '/' + (fileName || '')
			});
		}
		return '';
	}
	
	const isOT = !!(file.ot || file.ot_level || (typeof file.category === 'string' && file.category.toUpperCase() === 'OT'));
	const hasOnline = !!(file.o === 1 || file.online === 1);
	const isDoc = extLower === 'doc' || extLower === 'docx';
	const p = filePath.replace(/^\//, '').replace(/\/+/, '/');
	const n = fileName;
	const baseUrl = window.location.origin;
	
	let previewUrl = '';
	const rel = `${p}${p ? '/' : ''}${n}`;
	if (isOT) {
		previewUrl = `${baseUrl}/library/${encodeFilePath(rel)}?ot=1`;
	} else if (hasOnline || isDoc) {
		// For doc/docx files, always use /online/ URL for preview (never /library/ which downloads)
		previewUrl = `${baseUrl}/online/${encodeFilePath(rel)}`;
	} else {
		previewUrl = `${baseUrl}/library/${encodeFilePath(rel)}`;
	}
	
	return previewUrl;
}

